import api from '../../api';
import MembroBanca, { PapelNome, StatusMembroNome } from '../../model/banca/MembroBanca';
import Pessoa from '../../model/Pessoa';

interface PessoaConsultaDisponibilidade{
    pessoa: Pessoa;
    papelMembro: PapelNome;
}

export class MembrosBancaViewModel {

    static async patchSubstituicaoMembro(processoId: string, bancaId: string, membroId: string, pessoaId: string): Promise<void> {
        try {
            const requestBody = {
                membroId: membroId,
                pessoaId: pessoaId
            };
            await api.patch(`/processos/${processoId}/bancas/${bancaId}/membros/substituicao`, requestBody);
        } catch (error) {
            console.error("Erro ao substituir membro:", error);
            throw error;
        }
    }

    static async confirmaMembro(token: string): Promise<boolean> {
        try {
            await api.patch(`/emails/convocacao?token=${token}`);
            return true;
        } catch (error) {
            console.error('Erro ao confirmar membro:', error);
            return false;
        }
    }

    /*
    static async getMembros(processoId: string, bancaId: string): Promise<MembroBanca[]> {
        try {
            const response = await api.get(`/processos/${processoId}/bancas/${bancaId}/membros`);            
            return response.data as MembroBanca[];
        } catch (error) {
            console.error("Error fetching membros:", error);
            throw error;
        }
    }
    */

    static async getMembroById(membroId: string): Promise<MembroBanca> {
        try {            
            const response = await api.get(`/processos/bancas/membros/${membroId}`)
            const membro = response.data            
            return membro as MembroBanca;
        } catch (error) {
            console.error("Erro ao buscar membros:", error);
            throw error;
        }
    }

    static async getMembros(processoId: string, bancaId: string, confirmados: boolean): Promise<MembroBanca[]> {
        try {
            const response = await api.get(`/processos/${processoId}/bancas/${bancaId}/membros`, {
                params: { confirmados: confirmados }
            });
            const membros = response.data.map((membro: any) => {
                const { bancaResponse, ...membroSemBancaResponse } = membro;
                return membroSemBancaResponse;
            });
            return membros as MembroBanca[];
        } catch (error) {
            console.error("Erro ao buscar membros:", error);
            throw error;
        }
    }

    static async getMembrosCompletos(processoId: string, bancaId: string, verificarDuplicidade: boolean = false): Promise<MembroBanca[]> {
    try {
        const params = verificarDuplicidade ? { verificarDuplicidade: true } : {};
        const response = await api.get(`/processos/${processoId}/bancas/${bancaId}/membros`, { params });
        const membros = response.data.map((membro: any) => {
            // Extrai os dados básicos do membro
            const membroFormatado: MembroBanca = {
                id: membro.id,
                pessoa: {
                    id: membro.pessoa.id,
                    nome: membro.pessoa.nome,
                    cpf: membro.pessoa.cpf,
                    email: membro.pessoa.email,
                    vinculo: {
                        id: membro.pessoa.vinculo.id,
                        nome: membro.pessoa.vinculo.nome
                    },
                    matricula: membro.pessoa.matricula,
                    ativo: membro.pessoa.ativo,
                    emOutraBanca: membro.pessoa.emOutraBanca,
                    isMembroCavu: membro.pessoa.isMembroCavu,
                    jaConsultadoMesmoEditalChamada: membro.jaConsultadoMesmoEditalChamada || false,
                    jaDefinidoMesmoEditalChamada: membro.jaDefinidoMesmoEditalChamada || false
                },
                papelMembro: {
                    id: membro.papelMembro.id,
                    nome: this.mapPapelNome(membro.papelMembro.nome)
                },
                statusMembro: this.mapStatusMembroNome(membro.statusMembro),
                isDefinidoEmBancaDeOutroTipo: membro.isDefinidoEmBancaDeOutroTipo,
                telefone: membro.telefone,
                isPresidente: membro.isPresidente
            };
            return membroFormatado;
        });
        return membros;
    } catch (error) {
        console.error("Erro ao buscar membros completos:", error);
        throw error;
    }
}


    static async postMembros(processoId: string, bancaId: string, membros: MembroBanca[], categoriaEdital: string): Promise<MembroBanca[]> {
        try {
            const membrosFormatado = membros.map(membro => ({
                pessoa: {
                    id: membro.pessoa.id,
                    nome: membro.pessoa.nome,
                    vinculo: {
                        id: membro.pessoa.vinculo.id,
                        nome: membro.pessoa.vinculo.nome,
                    },
                    cpf: membro.pessoa.cpf,
                    email: membro.pessoa.email,
                },
                papelMembro: membro.papelMembro.nome
            }));
            const response = await api.post(`/processos/${processoId}/bancas/${bancaId}/membros`, membrosFormatado, { params: { categoria: categoriaEdital } });
            return response.data;
        } catch (error) {
            console.error("Error posting membros:", error);
            throw error;
        }
    }

    static async postMembrosPorPessoa(
        processoId: string,
        bancaId: string,
        pessoasSelecionadasAgora: [Pessoa, PapelNome][],
        categoriaEdital: string
    ): Promise<MembroBanca[]> {
        try {
            const membrosFormatado: any[] = []
            pessoasSelecionadasAgora.forEach(psa => {
                const pessoa = psa[0];
                const papel = psa[1];
                membrosFormatado.push(
                    {
                        pessoa: {
                            id: pessoa.id,
                            nome: pessoa.nome,
                            vinculo: {
                                id: pessoa.vinculo.id,
                                nome: pessoa.vinculo.nome,
                            },
                            cpf: pessoa.cpf,
                            email: pessoa.email,
                        },
                        papelMembro: papel
                    }
                )
            })
            // Faz a requisição ao backend
            const response = await api.post(`/processos/${processoId}/bancas/${bancaId}/membros`, membrosFormatado, { params: { categoria: categoriaEdital } });

            return response.data;
        } catch (error) {
            console.error("Error posting membros:", error);
            throw error;
        }
    }

    

    static async enviarConsultaDisponibilidade(
        processoId: string,
        bancaId: string,
        pessoasSelecionadas: PessoaConsultaDisponibilidade[],
        categoriaEdital: string
    ): Promise<MembroBanca[]> {
        try {            
            const response = await api.post(`/processos/${processoId}/bancas/${bancaId}/membros`, pessoasSelecionadas, { params: { categoria: categoriaEdital } });
            return response.data;
        } catch (error) {
            console.error("Error posting membros:", error);
            throw error;
        }
    }

    static async patchMembros(processoId: string, bancaId: string, membros: MembroBanca[]): Promise<MembroBanca[]> {
        try {
            const response = await api.patch(`/processos/${processoId}/bancas/${bancaId}/membros`, membros);
            return response.data;
        } catch (error) {
            console.error("Error patching membros:", error);
            throw error;
        }
    }


    static mapPapelNome = (nome: string): PapelNome => {
        switch (nome) {
            case 'Titular':
                return PapelNome.TITULAR;
            case 'Suplente':
                return PapelNome.SUPLENTE;
            case 'Técnico':
                return PapelNome.TECNICO;
            case 'Secretário':
                return PapelNome.SECRETARIO;
            default:
                throw new Error(`Unknown PapelNome: ${nome}`);
        }
    };

    static mapStatusMembroNome = (status: string): StatusMembroNome => {
        switch (status) {
            case 'CONSULTADO':
                return StatusMembroNome.CONSULTADO;
            case 'CONFIRMADO':
                return StatusMembroNome.CONFIRMADO;
            case 'DEFINIDO':
                return StatusMembroNome.DEFINIDO;
            case 'CONVOCADO':
                return StatusMembroNome.CONVOCADO;
            case 'CONVOCACAO_CONFIRMADA':
                return StatusMembroNome.CONVOCACAO_CONFIRMADA;
            default:
                throw new Error(`Unknown StatusMembroNome: ${status}`);
        }
    };
    

}
